import { useCallback, type ReactNode } from 'react';
import { Link, matchRoutes } from 'react-router';
import { usePermissions } from '@/auth/useAuth';
import { isFeatureOn, useFeatures, type FeatureSet } from '@/lib/features';
import { hasAnyPerm, type PermissionSet } from '@/lib/permissions';
import { APP_ROUTES, type AppRoute } from './registry';

/** A registry route the user can open: its feature is on for the company and the user holds one of its permissions. */
export function routeAvailable(route: Pick<AppRoute, 'perms' | 'feature'>, perms: PermissionSet, features: FeatureSet): boolean {
  return isFeatureOn(features, route.feature) && hasAnyPerm(perms, route.perms);
}

let matchTable: { path: string; route: AppRoute }[] | null = null;
function table() {
  // Built lazily: registry ↔ dashboard modules import each other through lazy pages.
  matchTable ??= APP_ROUTES.map((route) => ({ path: `/${route.path}`, route }));
  return matchTable;
}

/** The registry route a URL (path, optional ?query / #hash) points to, using the router's own ranking. */
export function routeFor(to: string): AppRoute | undefined {
  const pathname = to.split(/[?#]/, 1)[0] || '/';
  const match = matchRoutes(table(), pathname)?.[0];
  return (match?.route as { route: AppRoute } | undefined)?.route;
}

/**
 * Whether an in-app link should be clickable: false when the target route is hidden (feature off or no permission).
 * Unknown paths stay clickable (the router shows its 404).
 */
export function canLinkTo(to: string, ctx: { perms: PermissionSet; features: FeatureSet }): boolean {
  const route = routeFor(to);
  return !route || routeAvailable(route, ctx.perms, ctx.features);
}

export function useCanLinkTo(): (to: string) => boolean {
  const perms = usePermissions();
  const { features } = useFeatures();
  return useCallback((to: string) => canLinkTo(to, { perms, features }), [perms, features]);
}

/** <Link> when the target is available, otherwise its content as plain text (no dead end for the user). */
export function GuardedLink({ to, className, plainClassName, children }: { to: string; className?: string; plainClassName?: string; children: ReactNode }) {
  const can = useCanLinkTo();
  if (!can(to)) return <span className={plainClassName}>{children}</span>;
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}
